import { inject, Injectable, resource, signal } from "@angular/core";
import { firstValueFrom, map, Observable } from "rxjs";
import {
  ApiListResponse,
  ApiSingleResponse,
} from "src/app/core/services/api.types";
import { ClassJaxService } from "src/app/core/services/classjax.service";
import {
  HelpGuide,
  HelpGuideStep,
  HelpGuideWithSteps,
} from "./help.types";

@Injectable({ providedIn: "root" })
export class HelpService {
  private api = inject(ClassJaxService);

  /**
   * Last guide the user was viewing in the offcanvas. Memory-only — survives
   * close/reopen of the panel within a window, but not page refresh. Cleared
   * when the user explicitly returns to the categories list.
   */
  readonly lastSlug = signal<string | null>(null);

  readonly guides = resource({
    loader: async () => {
      const res = await firstValueFrom(
        this.api.get<ApiListResponse<HelpGuide>>("/help/guides", {
          status: "published",
        }),
      );
      return res.data;
    },
  });

  getById(id: string): Observable<HelpGuideWithSteps> {
    return this.api
      .get<ApiSingleResponse<HelpGuideWithSteps>>(`/help/guides/${id}`)
      .pipe(map((res) => res.data));
  }

  getBySlug(slug: string): Observable<HelpGuideWithSteps> {
    return this.api
      .get<ApiSingleResponse<HelpGuideWithSteps>>(
        `/help/guides/by-slug/${slug}`,
      )
      .pipe(map((res) => res.data));
  }

  updateStep(
    guideId: string,
    stepId: string,
    patch: Partial<
      Pick<
        HelpGuideStep,
        | "step_text"
        | "step_description"
        | "step_index"
        | "crop_offset_x"
        | "crop_offset_y"
        | "zoom_pct"
        | "click_x"
        | "click_y"
      >
    >,
  ): Promise<HelpGuideStep> {
    return firstValueFrom(
      this.api
        .patch<ApiSingleResponse<HelpGuideStep>>(
          `/help/guides/${guideId}/steps/${stepId}`,
          patch,
        )
        .pipe(map((res) => res.data)),
    );
  }

  deleteStep(guideId: string, stepId: string): Promise<void> {
    return firstValueFrom(
      this.api
        .delete<{ success: boolean }>(`/help/guides/${guideId}/steps/${stepId}`)
        .pipe(map(() => undefined)),
    );
  }
}
